import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Users, FileText, Calendar, Image, MessageSquare, GraduationCap, Heart, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalMembers: 0,
    pendingMembers: 0,
    approvedMembers: 0,
    totalNews: 0,
    totalEvents: 0,
    totalGallery: 0,
    totalFeedback: 0,
    pendingFeedback: 0,
    conclaveRegistrations: 0,
    scholarshipDonors: 0,
    scholarshipApplications: 0,
    totalInstitutions: 0,
    pendingInstitutions: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [members, news, events, gallery, feedback, conclave, donors, applications, institutions] = await Promise.all([
        axios.get(`${API}/members`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/news`).catch(() => ({ data: [] })),
        axios.get(`${API}/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/feedback`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/conclave-registrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/scholarship/donors`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/scholarship/applications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/institutional-members`, { headers }).catch(() => ({ data: { institutions: [] } })),
      ]);

      const institutionsList = institutions.data.institutions || [];
      
      setStats({
        totalMembers: members.data.length,
        pendingMembers: members.data.filter(m => m.status === 'pending').length,
        approvedMembers: members.data.filter(m => m.status === 'approved').length,
        totalNews: news.data.length,
        totalEvents: events.data.length,
        totalGallery: gallery.data.length,
        totalFeedback: feedback.data.length,
        pendingFeedback: feedback.data.filter(f => f.status === 'pending').length,
        conclaveRegistrations: conclave.data.length,
        scholarshipDonors: donors.data.length,
        scholarshipApplications: applications.data.length,
        totalInstitutions: institutionsList.length,
        pendingInstitutions: institutionsList.filter(i => i.status === 'pending').length,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Members',
      value: stats.totalMembers,
      subtext: `${stats.pendingMembers} pending approval`,
      icon: Users,
      color: 'bg-blue-500',
      link: '/admin/members',
    },
    {
      title: 'Conclave Registrations',
      value: stats.conclaveRegistrations,
      subtext: 'Khatri Conclave 2026',
      icon: Calendar,
      color: 'bg-purple-500',
      link: '/admin/conclave-registrations',
    },
    {
      title: 'Scholarship Donors',
      value: stats.scholarshipDonors,
      subtext: 'Shiksha Mitra / Parammitra',
      icon: Heart,
      color: 'bg-red-500',
      link: '/admin/scholarship-donors',
    },
    {
      title: 'Scholarship Applications',
      value: stats.scholarshipApplications,
      subtext: 'Student applications',
      icon: GraduationCap,
      color: 'bg-green-500',
      link: '/admin/scholarship-applications',
    },
    {
      title: 'Institutional Members',
      value: stats.totalInstitutions,
      subtext: `${stats.pendingInstitutions} pending approval`,
      icon: Building2,
      color: 'bg-orange-500',
      link: '/admin/institutional-members',
    },
    {
      title: 'News Articles',
      value: stats.totalNews,
      subtext: 'Published articles',
      icon: FileText,
      color: 'bg-amber-500',
      link: '/admin/news',
    },
    {
      title: 'Events',
      value: stats.totalEvents,
      subtext: 'Upcoming & past',
      icon: Calendar,
      color: 'bg-indigo-500',
      link: '/admin/events',
    },
    {
      title: 'Gallery Items',
      value: stats.totalGallery,
      subtext: 'Photos & videos',
      icon: Image,
      color: 'bg-pink-500',
      link: '/admin/gallery',
    },
    {
      title: 'Feedback',
      value: stats.totalFeedback,
      subtext: `${stats.pendingFeedback} pending`,
      icon: MessageSquare,
      color: 'bg-teal-500',
      link: '/admin/feedback',
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="font-serif font-bold text-3xl text-primary mb-2">Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome back, {user?.fullName || 'Admin'}! Here&apos;s an overview of your portal.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-12 w-12 bg-muted rounded-full mb-4"></div>
              <div className="h-6 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card) => (
            <Link key={card.title} to={card.link}>
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className={`w-12 h-12 ${card.color} rounded-full flex items-center justify-center mb-4`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-foreground mb-1">{card.value}</h3>
                <p className="font-semibold text-foreground">{card.title}</p>
                <p className="text-sm text-muted-foreground">{card.subtext}</p>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="font-serif font-bold text-2xl text-primary mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/admin/members">
            <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Approve Members</p>
                  <p className="text-sm text-muted-foreground">{stats.pendingMembers} pending approvals</p>
                </div>
              </div>
            </Card>
          </Link>
          <Link to="/admin/scholarship-applications">
            <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <GraduationCap className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-semibold">Review Scholarship Applications</p>
                  <p className="text-sm text-muted-foreground">{stats.scholarshipApplications} applications</p>
                </div>
              </div>
            </Card>
          </Link>
          <Link to="/admin/feedback">
            <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-secondary" />
                <div>
                  <p className="font-semibold">Respond to Feedback</p>
                  <p className="text-sm text-muted-foreground">{stats.pendingFeedback} pending responses</p>
                </div>
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
